/**
 * The option lists the page this replaces used, carried over verbatim from s.web's
 * `views/_components/LeadForm/util.js` — `getApartmentConfigurationOptions` and
 * `getLeaseDurationOptions`. The values are what the API stores, so they are not free
 * text: changing a label without changing its value silently splits the reporting.
 *
 * ⚠️  `label`/`description` are TRANSLATION KEYS; `value` is the stored English
 * string and must never be run through `t()`. That split is the whole point — the
 * reader sees a localised label while the API keeps receiving the value it indexes
 * on, so translating the page cannot move any reporting.
 */
export interface RoomOption {
  /** Translation key for the option's label. */
  label: string;
  /** Stored verbatim by the API — NOT translated. */
  value: string;
  /** Translation key for the supporting line. */
  description?: string;
}

const ROOMS_COMMON: RoomOption[] = [
  {
    label: "groupbooking.v2.rooms.ensuite.label",
    value: "Ensuite Room",
    description: "groupbooking.v2.rooms.ensuite.description",
  },
  {
    label: "groupbooking.v2.rooms.studio.label",
    value: "Studio Room",
    description: "groupbooking.v2.rooms.studio.description",
  },
  {
    label: "groupbooking.v2.rooms.shared.label",
    value: "Shared Room",
    description: "groupbooking.v2.rooms.shared.description",
  },
  {
    label: "groupbooking.v2.rooms.private.label",
    value: "Private Room",
    description: "groupbooking.v2.rooms.private.description",
  },
];

const ROOMS_US: RoomOption[] = [
  { label: "groupbooking.v2.rooms.studioapartment.label", value: "Studio Apartment" },
  { label: "groupbooking.v2.rooms.shared.label", value: "Shared Room" },
  { label: "groupbooking.v2.rooms.private.label", value: "Private Room" },
];

export const getRoomOptions = (country?: string): RoomOption[] =>
  country === "us" ? ROOMS_US : ROOMS_COMMON;

/**
 * Duration options as `{ value, label }` rather than bare strings.
 *
 * Same split as `RoomOption`: `value` is what the API stores and what the `<option>`
 * submits, `label` is the translation key the reader sees.
 */
export interface DurationOption {
  /** Stored verbatim by the API — NOT translated. */
  value: string;
  /** Translation key for the option's label. */
  label: string;
}

const DURATIONS_US: DurationOption[] = [
  { value: "0 - 4 months", label: "groupbooking.v2.duration.0to4months" },
  { value: "5 - 8 months", label: "groupbooking.v2.duration.5to8months" },
  { value: "8+ months", label: "groupbooking.v2.duration.8plusmonths" },
];

const DURATIONS_COMMON: DurationOption[] = [
  { value: "0 - 25 Weeks", label: "groupbooking.v2.duration.0to25weeks" },
  { value: "26 - 40 Weeks", label: "groupbooking.v2.duration.26to40weeks" },
  { value: "41 - 45 Weeks", label: "groupbooking.v2.duration.41to45weeks" },
  { value: "45+ Weeks", label: "groupbooking.v2.duration.45plusweeks" },
];

export const getDurationOptions = (country?: string): DurationOption[] =>
  country === "us" ? DURATIONS_US : DURATIONS_COMMON;
